import type { Dispatch } from 'react';
import type { FlightExplorerOffer } from './search_source.js';
import { liveOfferForPick } from '../results/flight_identity.js';
import { offersOf } from './explorer_legs.js';
import { legSearchParams } from './explorer_request.js';
import type { ExplorerEvent, ExplorerState } from './explorer_state.js';
import type { FlightSearchParams } from './search_source.js';

/** O IO que o "Tentar de novo" dispara — o espelho de IO do `FlightExplorer`. */
export interface ExplorerRetryIo {
  runLegSearch: (leg: number, params: FlightSearchParams) => void;
  /** `onLanded` só roda quando a busca pousou E ainda é a corrente da perna. */
  runSearch: (
    params: FlightSearchParams,
    onLanded?: (offers: FlightExplorerOffer[]) => void,
  ) => void;
  chooseOutbound: (offerId: string) => void;
}

/** A volta do round trip falhou porque a busca da ida venceu no fornecedor
 *  (#5588)? Repetir a return-search sobre ela devolveria vazio para sempre. */
function returnSearchExpired(state: ExplorerState): boolean {
  const leg = state.legs[1];
  return (
    state.request.mode === 'round_trip' &&
    state.activeLeg === 1 &&
    leg?.phase === 'error' &&
    leg.failure === 'search_expired'
  );
}

/**
 * A busca vencida se recupera sozinha (#5588): refaz a busca INTEIRA com os
 * critérios do pedido (rota, datas, cabine, viajante — nada é pedido de novo),
 * acha nela a MESMA ida pela identidade do voo (as refs mudam de sessão, os
 * segmentos não: `liveOfferForPick` com a ida antiga como semente) e dispara a
 * return-search sobre o handle NOVO — a pessoa volta direto à lista de voltas.
 * Se a ida não voltou, fica na lista de idas nova com o aviso (`outbound_gone`).
 *
 * Enquanto a busca corre, a fase 1 mostra o esqueleto: a página do erro (e o
 * botão dela) sai de cena no mesmo clique, então não há clique repetido.
 */
function refreshExpiredSearch(
  state: ExplorerState,
  io: ExplorerRetryIo,
  send: Dispatch<ExplorerEvent>,
  pick: { offerId: string },
): void {
  const previous = offersOf(state.legs[0]).filter((offer) => offer.offerId === pick.offerId);
  io.runSearch(legSearchParams(state.request, 0), (offers) => {
    const same = liveOfferForPick(pick, offers, previous);
    if (same) io.chooseOutbound(same.offerId);
    else send({ t: 'outbound_gone' });
  });
}

/** Re-dispara a busca que a fase corrente perdeu (erro → Tentar de novo). */
export function makeExplorerRetry(
  state: ExplorerState,
  io: ExplorerRetryIo,
  send: Dispatch<ExplorerEvent>,
): () => void {
  return () => {
    const outbound = state.chosen[0];
    if (outbound && returnSearchExpired(state)) {
      refreshExpiredSearch(state, io, send, outbound);
      return;
    }
    if (state.request.mode === 'round_trip' && state.activeLeg === 1 && outbound) {
      io.chooseOutbound(outbound.offerId);
      return;
    }
    if (state.request.mode === 'multi_leg') {
      io.runLegSearch(state.activeLeg, legSearchParams(state.request, state.activeLeg));
      return;
    }
    io.runSearch(legSearchParams(state.request, 0));
  };
}
